import { type FormEvent, useState } from "react";
import { unlock } from "./api";

// Password prompt for password-protected shares. Posts to /unlock and
// asks the parent (PublicSharePage) to refetch the manifest on success
// — which it'll receive without `requiresPassword` thanks to the
// HttpOnly cookie set by the server.

export function PasswordGate(props: {
  token: string;
  title: string | null;
  note: string | null;
  onUnlocked: () => void;
}) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await unlock(props.token, password);
      props.onUnlocked();
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      const message =
        code === "BAD_PASSWORD"
          ? "Incorrect password."
          : code === "TOO_MANY_ATTEMPTS"
          ? "Too many attempts. Try again in a few minutes."
          : (err as Error).message ?? "Could not unlock.";
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-sm"
      >
        <h1 className="text-lg font-semibold">
          {props.title ?? "Password required"}
        </h1>
        {props.note ? (
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{props.note}</p>
        ) : (
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            This share is password-protected.
          </p>
        )}
        <label className="block mt-5">
          <span className="text-xs uppercase tracking-wide text-zinc-500">
            Password
          </span>
          <input
            autoFocus
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </label>
        {error ? (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : null}
        <button
          type="submit"
          disabled={submitting || password.length === 0}
          className="mt-5 w-full rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Unlocking…" : "Unlock"}
        </button>
      </form>
    </div>
  );
}
